import React, {useEffect, useState} from 'react';
import {Paper, Tooltip} from '@mui/material';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import RoofingIcon from '@mui/icons-material/Roofing';
import ParkIcon from '@mui/icons-material/Park';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useMapContext } from '../../contexts/MapContext';
import { useLIDContext } from "../../contexts/LIDContext";

const IS_MAIN_BRANCH = process.env.REACT_APP_OPENROUTER_API_KEY === "none";

const LIDSummary = () => {
    const {
        selectedFeature,
        handleMapClick,
        mapRef,
        subcatchmentNameJSON,
    } = useMapContext();

    const {
        devPlanContent,
        aggregatedCounts,
    } = useLIDContext();

    const selectedFeatureData = selectedFeature.Data;

    useEffect(() => {
        const scrollToItem = () => {
            // Scroll to this subcatchment in the summary
            if (selectedFeatureData && selectedFeatureData.NAME) {
                const element = document.getElementById(`${selectedFeatureData.NAME}-summary-item`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
        scrollToItem();
    }, [selectedFeatureData]);


    const handleSubcatchmentClick = (subcatchment) => {
        const feature = mapRef.current.queryRenderedFeatures(undefined, {
            layers: ['subcatchments'],
            filter: ['==', ['get', 'NAME'], subcatchment]
        });
        console.log(feature[0]);
        handleMapClick(null, feature);
    };


    const renderLIDCountWithIcon = (lidType, count) => {
        const getIcon = (type) => {
            switch(type) {
                case 'RG1': return <LocalFloristIcon />;
                case 'GR1': return <RoofingIcon />;
                case 'Planter': return <ParkIcon />;
                case 'RB12': return <OilBarrelIcon />;
                default: return null;
            }
        };
    
        return (
            <div key={lidType} style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
                {getIcon(lidType)}
                <span style={{ marginLeft: '5px' }}>{count}</span>
            </div>
        );
    };

    const formatLIDData = (lidData) => {
        const lidTypeMapping = {
            RG1: "Rain Garden",
            Planter: "Tree Planter",
            GR1: "Green Roof",
            RB12: "Rain Barrel"
        };

        return Object.entries(lidData).map(([lidType, data]) => {
            const count = data.n_replicate;
            const area = data.area;
            const lidName = lidTypeMapping[lidType] || lidType;
            const pluralSuffix = count > 1 ? 's' : '';
            if (lidType === 'Planter') {
                return `${count} ${lidName}${pluralSuffix}`;
            } else {
                return `${count} ${lidName}${pluralSuffix} (area: ${area} sq ft)`;
            }
        }).join(', ');
    };

    return (
        <div
            style={{
                position: 'absolute',
                bottom: 20,
                left: 50,
                width: '300px',
                height: '250px',
                backgroundColor: '#071317',
                opacity: 0.9,
                zIndex: 10,
                borderRadius: '5px',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >

            <div style={{display: 'flex', alignItems: 'center', justifyContent:"space-between", gap: '10px'}}>
                <h4 style={{color: '#94cfec', margin: '0 0 0 0'}}> Development Plan:</h4>
                {!IS_MAIN_BRANCH && (
                    <Tooltip title="Download the raw development plan data." placement="top">
                        <FileDownloadIcon
                            style={{
                                color: '#94cfec',
                                cursor: 'pointer',
                                fontSize: '18px',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#94CFEC';
                            }}
                            onClick={() => {
                                const data = JSON.stringify(devPlanContent, null, 2);
                                const blob = new Blob([data], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = 'devPlanData.json';

                                // Append to body, click, and clean up
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }}
                        />
                    </Tooltip>
                )}
            </div>
            <Paper elevation={3} style={{ 
                padding: '10px', 
                backgroundColor: '#1e2a30', 
                color: '#94cfec', 
                fontSize: '14px', 
                maxHeight: '90%', 
                minHeight: '90%',
                overflow: 'auto' 
            }}>
                <h4 style={{ color: '#94cfec', margin: '0 0 0 0' }}> Total: </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {Object.entries(aggregatedCounts).map(([lidType, count]) => 
                        renderLIDCountWithIcon(lidType, count)
                    )}
                </div>
                <h4 style={{ color: '#94cfec', margin: '0 0 0 0' }}> Subcatchment Breakdown: </h4>
                {Object.entries(devPlanContent).map(([subcatchment, lidData]) => (
                    <p  
                        key={subcatchment}
                        id={`${subcatchment}-summary-item`}
                        style={{ 
                            color: selectedFeatureData && selectedFeatureData.NAME === subcatchment ? '#000' : '#94cfec',
                            margin: '5px 0', 
                            padding: '2px 5px',
                            backgroundColor: selectedFeatureData && selectedFeatureData.NAME === subcatchment ? '#67ea04' : 'transparent',
                            borderRadius: '3px'
                        }}
                        onClick={() => handleSubcatchmentClick(subcatchment)}
                    >
                        <strong>{subcatchmentNameJSON[subcatchment] || subcatchment}:</strong> {formatLIDData(lidData)}
                    </p>
                ))}
            </Paper>
        </div>
    );
};

export default LIDSummary;